/**
 * Time utilities for session status detection
 */

/**
 * Check if a session is currently happening
 * @param {Object} session - Session object with startTime and endTime
 * @param {Date} currentTime - Optional current time for simulation (defaults to now)
 * @returns {boolean} - True if session is currently in progress
 */
export function isSessionHappeningNow(session, currentTime = new Date()) {
  const now = currentTime
  const startTime = new Date(session.startTime)
  const endTime = new Date(session.endTime)
  
  return now >= startTime && now < endTime
}

/**
 * Check if a session is coming up soon
 * @param {Object} session - Session object with startTime
 * @param {number} minutesAhead - How many minutes ahead to check (default 90)
 * @param {Date} currentTime - Optional current time for simulation (defaults to now)
 * @returns {boolean} - True if session starts within the specified time window
 */
export function isSessionComingSoon(session, minutesAhead = 90, currentTime = new Date()) {
  const now = currentTime
  const startTime = new Date(session.startTime)
  const timeDiff = startTime - now
  
  // Session is coming up if it starts within the next X minutes and hasn't started yet
  return timeDiff > 0 && timeDiff <= (minutesAhead * 60 * 1000)
}

/**
 * Get sessions happening now
 * @param {Array} sessions - Array of session objects
 * @param {Date} currentTime - Optional current time for simulation (defaults to now)
 * @returns {Array} - Sessions currently in progress
 */
export function getCurrentSessions(sessions, currentTime = new Date()) {
  return sessions.filter(session => isSessionHappeningNow(session, currentTime))
}

/**
 * Get upcoming sessions
 * @param {Array} sessions - Array of session objects
 * @param {number} minutesAhead - How many minutes ahead to check
 * @param {Date} currentTime - Optional current time for simulation (defaults to now)
 * @returns {Array} - Sessions starting soon, sorted by start time
 */
export function getUpcomingSessions(sessions, minutesAhead = 90, currentTime = new Date()) {
  return sessions
    .filter(session => isSessionComingSoon(session, minutesAhead, currentTime))
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
}

/**
 * Format relative time for dashboard display
 * @param {string} dateTime - ISO datetime string
 * @param {Date} currentTime - Optional current time for simulation (defaults to now)
 * @returns {string} - Human readable relative time
 */
export function formatRelativeTime(dateTime, currentTime = new Date()) {
  const now = currentTime
  const target = new Date(dateTime)
  const diffMs = target - now
  const diffMins = Math.round(diffMs / (1000 * 60))
  
  if (diffMins < 0) {
    return `Started ${Math.abs(diffMins)} min ago`
  } else if (diffMins === 0) {
    return 'Starting now'
  } else if (diffMins < 60) {
    return `Starts in ${diffMins} min`
  } else {
    const hours = Math.floor(diffMins / 60)
    const mins = diffMins % 60
    if (mins === 0) {
      return `Starts in ${hours}h`
    }
    return `Starts in ${hours}h ${mins}m`
  }
}

/**
 * Calculate time remaining until session ends
 * @param {Object} session - Session object with endTime
 * @param {Date} currentTime - Optional current time for simulation (defaults to now)
 * @returns {string} - Human readable time remaining (e.g., "ends in 15 min 30s")
 */
export function formatTimeRemaining(session, currentTime = new Date()) {
  const now = currentTime
  const endTime = new Date(session.endTime)
  const diffMs = endTime - now
  
  if (diffMs <= 0) {
    return 'ended'
  }
  
  const totalSeconds = Math.floor(diffMs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  
  if (hours > 0) {
    if (minutes === 0) {
      return `ends in ${hours}h ${seconds}s`
    }
    return `ends in ${hours}h ${minutes}m ${seconds}s`
  } else if (minutes > 0) {
    return `ends in ${minutes}m ${seconds}s`
  } else {
    return `ends in ${seconds}s`
  }
}

/**
 * Check if a session has already ended
 * @param {Object} session - Session object with endTime
 * @param {Date} currentTime - Optional current time for simulation (defaults to now)
 * @returns {boolean} - True if session has ended
 */
export function isSessionEnded(session, currentTime = new Date()) {
  const now = currentTime
  const endTime = new Date(session.endTime)
  return now >= endTime
} 